"use client";

import { ArrowRight } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Infrastructure() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    index: "01",
    title: "NETWORK",
    subtitle: "High-Performance Connectivity",
    description: "High-speed copper or fiber-based infrastructure with redundant internet, automatic ISP failover, and high-performance Wi-Fi for seamless building-to-building connectivity.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/network_infrastructure.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/network_infrastructure.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/network_infrastructure.webp-wvc-sizes",
    alt: "Fiber optic cables and network server racks"
  }, {
    index: "02",
    title: "SURVEILLANCE",
    subtitle: "Intelligent Security",
    description: "Expandable video storage with AI-powered alerts, car tag and face recognition, and mobile app integration for real-time monitoring across multiple screens.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/surveillance_system.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/surveillance_system.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/surveillance_system.webp-wvc-sizes",
    alt: "Security camera monitoring interface with digital overlays"
  }, {
    index: "03",
    title: "ACCESS CONTROL",
    subtitle: "Secure Entry Systems",
    description: "NFC-based entry using cards, tags, or mobile devices with Apple Passkey support, integrated with video-enabled door readers and automated gate recognition.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/access_control.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/access_control.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715292/img/access_control.png-wvc-sizes",
    alt: "Modern digital door access reader with NFC technology"
  }];
  return <section data-section-id="2604" id="core-infrastructure-solutions" className="bg-background text-foreground py-32 md:py-48 lg:py-60 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs tracking-[0.2em] mb-8 md:mb-12" style={{
            color: "var(--chart-1)"
          }}>
              SOLUTIONS / CORE SYSTEMS
            </p>
            <h2 className="font-default text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tight">
              UNIFIED
              <br />
              FOR <span className="font-serif italic font-normal">BUSINESS</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6 flex lg:items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-[480px]">
              Betaquest, LLC specializes in unified IT solutions tailored to your needs. Our systems integrate seamlessly to improve performance, security, and scalability.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((pillar, i) => <PillarCard key={i} pillar={pillar} delay={i * 200} index={i} />)}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  delay,
  index
}: {
  pillar: {
    index: string;
    title: string;
    subtitle: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  delay: number;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState({
    x: 50,
    y: 50
  });
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({
      x: (e.clientX - rect.left) / rect.width * 100,
      y: (e.clientY - rect.top) / rect.height * 100
    });
  };
  return <div data-index={index} ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <div ref={cardRef} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="group relative h-[640px] bg-card border border-border overflow-hidden transition-all duration-500" style={{
      borderColor: isHovered ? "var(--chart-1)" : "var(--border)"
    }}>
        {}
        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" style={{
        background: `radial-gradient(circle 320px at ${cursor.x}% ${cursor.y}%, hsla(270, 100%, 64%, 0.15), transparent 70%)`
      }} />

        {}
        <div className="relative h-[60%] overflow-hidden">
          <img src={pillar.image} data-wvc-srcset={pillar.srcset} data-wvc-sizes={pillar.sizes} alt={pillar.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
          <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(180deg, transparent 0%, transparent 60%, hsla(260, 20%, 2%, 0.8) 100%)"
        }} />
          {}
          <div className="absolute top-6 left-6 z-10">
            <span className="font-mono text-xs tracking-[0.2em]" style={{
            color: "var(--chart-1)"
          }}>
              / {pillar.index}
            </span>
          </div>
        </div>

        {}
        <div className="relative h-[40%] p-6 md:p-8 flex flex-col justify-between">
          <div className="relative">
            <p className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground mb-3">
              {pillar.index} / 03
            </p>

            {}
            <div className="relative overflow-hidden">
              <h3 className="font-default text-2xl md:text-3xl font-bold tracking-tight transition-transform duration-500 ease-out group-hover:-translate-y-2">
                {pillar.title}
              </h3>
              <p className="font-mono text-xs tracking-[0.15em] mt-1 opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out" style={{
              color: "var(--chart-1)"
            }}>
                {pillar.subtitle}
              </p>
            </div>

            <p className="font-default text-sm leading-relaxed text-muted-foreground mt-4 line-clamp-3">
              {pillar.description}
            </p>
          </div>

          <div className="flex items-center justify-between mt-6">
            <Button asChild variant="link" className="font-mono text-xs tracking-[0.2em] p-0 h-auto text-foreground hover:no-underline group/btn">
              <Link to="/SolutionsPage">
                <span className="relative">
                  VIEW DETAILS
                  <span className="absolute bottom-0 left-0 h-px bg-current transition-all duration-500 w-0 group-hover/btn:w-full" />
                </span>
                <ArrowRight className="size-3 transition-transform duration-500 group-hover/btn:translate-x-1" />
              </Link>
            </Button>
            <div className="h-px flex-1 ml-6 transition-all duration-500" style={{
            background: isHovered ? "var(--chart-1)" : "var(--border)"
          }} />
          </div>
        </div>
      </div>
    </div>;
}